import { PricingTable, PricingTableKey } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { Button } from 'base/ui/button';
import { Icon } from 'base/ui/icon';
import { IconCheck, IconFigma, IconGroup, IconUser } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { LottiePlayer } from 'base/ui/lottie';
import { DividerLine } from 'base/ui/panel';
import { Toggle } from 'base/ui/toggle';
import {
  TextMedium,
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  Title1,
  TitlePricing,
} from 'base/ui/typography';
import { useToggle } from 'base/useToggle';
import { formatCentAmount } from 'blocks/auth/plan_buttons';
import {
  getBillingInterval,
  getHumanPlanName,
  getPlanName,
} from 'blocks/billing/plan_utils';
import iconGiftUrl from 'blocks/dashboard/icon-gift.file.svg';
import classNames from 'classnames';
import React from 'react';
import avatarsLottie from './avatars.lottie.json';
import { default as LogoDept } from './logos/dept.react.svg';
import { default as LogoIdeo } from './logos/ideo.react.svg';
import { default as LogoNike } from './logos/nike.react.svg';
import { default as LogoRokt } from './logos/rokt.react.svg';
import { default as LogoVmg } from './logos/vmg.react.svg';
import { default as LogoWebflow } from './logos/webflow.react.svg';
import planAvatarFree from './plan-card-avatar-free.webp';
import planAvatarPro from './plan-card-avatar-pro.webp';
import planAvatarStarter from './plan-card-avatar-starter.webp';
import planAvatarTeam1 from './plan-card-avatar-team-1.webp';
import planAvatarTeam2 from './plan-card-avatar-team-2.webp';
import planAvatarTeam3 from './plan-card-avatar-team-3.webp';
import styles from './PricingPage.css';

const Header = ({ showTeam }: { showTeam: boolean }) => {
  return (
    <Rows spacing={0}>
      <div className={styles.showOnMobile}>
        <Rows>
          <span className={styles.animation}>
            <LottiePlayer animationData={avatarsLottie} loop={false} />
          </span>
          <Title1>
            {showTeam
              ? 'Plans that evolve with your projects. Try with your team for free.'
              : 'Choose the plan that works for you.'}
          </Title1>
        </Rows>
      </div>
      {/* Getting a structure to work nicely on desktop and mobile is really hard, duplicating instead. */}
      <div className={styles.hideOnMobile}>
        {showTeam ? (
          <>
            {' '}
            <TitlePricing tag="span" align="center">
              Plans that evolve with your projects.
            </TitlePricing>
            <Inline spacing={6} xalign="center">
              <TitlePricing tag="span">
                Try with your{' '}
                <TitlePricing tag="span" color="gradient">
                  team
                </TitlePricing>
              </TitlePricing>
              <span className={styles.animation}>
                <LottiePlayer animationData={avatarsLottie} loop={false} />
              </span>
              <TitlePricing>for free.</TitlePricing>
            </Inline>
          </>
        ) : (
          <TitlePricing tag="span" align="center">
            Choose the plan that works for you.
          </TitlePricing>
        )}
      </div>
    </Rows>
  );
};
export function PricingPage({
  currentPlanKey,
  table,
  getOnClick,
  getHref,
  accountStore,
}: {
  accountStore: AccountStore;
  currentPlanKey?: PricingTableKey;
  table?: PricingTable;
  getOnClick?: (planKey: PricingTableKey) => () => void;
  getHref?: (planKey: PricingTableKey) => string;
}) {
  const activeReferralPromotion = accountStore.member?.activeReferralPromotion;
  const { value: yearly, toggle: toggleYearly } = useToggle(
    !activeReferralPromotion,
  );

  const showReferralPromotionBanner = activeReferralPromotion && !yearly;

  return (
    <div className={styles.container}>
      <Rows spacing={!showReferralPromotionBanner ? 36 : 16} xalign="center">
        <Header showTeam={!showReferralPromotionBanner} />
        {showReferralPromotionBanner && (
          <div className={styles.bannerContainer}>
            <Inline spacing={16} yalign="center">
              <img src={iconGiftUrl} className={styles.giftImage} />
              <TextRegularLight>
                You’ve been invited by {activeReferralPromotion?.referrerName}:{' '}
                <b>{activeReferralPromotion?.discountPercent}% off</b> your
                first month on <b>Starter and Pro (monthly only)</b>. Team and
                yearly plans excluded. Discount applied automatically.
              </TextRegularLight>
            </Inline>
          </div>
        )}
        <div className={styles.subHeader}>
          <div>
            <Rows spacing={8} xalign="start">
              <TextSmallLight color="grey">
                Teams already on Relume
              </TextSmallLight>
              <div className={styles.logos}>
                <Logo svg={LogoNike} />
                <Logo svg={LogoWebflow} />
                <Logo svg={LogoRokt} />
                <Logo svg={LogoIdeo} />
                <Logo svg={LogoDept} />
                <Logo svg={LogoVmg} />
              </div>
            </Rows>
          </div>
          <div className={styles.yearlyToggle}>
            <Inline spacing={10}>
              <TextRegular>Yearly discount (30%)</TextRegular>
              <Toggle value={yearly} onChange={toggleYearly} color="green" />
            </Inline>
          </div>
        </div>
      </Rows>
      <div className={styles.cards}>
        <Plan
          variant="tertiary"
          title={<TextMedium>Free</TextMedium>}
          icon={<Icon icon={IconUser} />}
          description="Conceptualize anything and bring your ideas to life. No cost, but with limits."
          offer={
            <OfferDetails
              table={table}
              currentPlanKey={currentPlanKey}
              offerPlanKey="free"
              tagline="/month for one person"
              variant="secondary"
              getHref={getHref}
              getOnClick={getOnClick}
            />
          }
          features={
            <FeatureList
              features={[
                { label: '1 project' },
                { label: '1 page' },
                { label: 'Share - Read-only link' },
                {
                  label: 'Export - Figma only',
                  iconsRight: <Icon icon={IconFigma} />,
                },
                { label: '30 Webflow components' },
                { label: '30 React components' },
                { label: '1,000+ Figma components' },
              ]}
            />
          }
          personaAvatarSrc={[planAvatarFree]}
          personaTitle="New Designer or Developer"
          personaDescription="New to the industry and Relume. Usual projects are basic 1-page landing pages, requiring little to no collaboration"
        />
        <Plan
          variant="secondary"
          title={
            <TextMedium>
              Relume{' '}
              <TextMedium color="gradient" tag="span">
                Starter
              </TextMedium>
            </TextMedium>
          }
          icon={<Icon icon={IconUser}></Icon>}
          description="Unlock extended functionality and turn those ideas into small websites."
          offer={
            <OfferDetails
              table={table}
              currentPlanKey={currentPlanKey}
              offerPlanKey={yearly ? 'starterYearly' : 'starterMonthly'}
              tagline="/month for one person"
              variant="primary"
              getHref={getHref}
              getOnClick={getOnClick}
            />
          }
          features={
            <FeatureList
              features={[
                { label: '1 project' },
                { label: '5 pages' },
                { label: 'Share - Commenting' },
                {
                  label: 'Export - Figma, Webflow & React',
                },
                { label: '1,000+ Webflow components' },
                { label: '1,000+ React components' },
                { label: '1,000+ Figma components' },
              ]}
            />
          }
          personaAvatarSrc={[planAvatarStarter]}
          personaTitle="Part-time Freelancer"
          personaDescription="A seasoned designer and dev with a full-time job, often takes on a medium sized website project, normally only needing one project per month"
        />
        <Plan
          variant="primary"
          title={
            <TextMedium>
              Relume{' '}
              <TextMedium color="gradient" tag="span">
                Pro
              </TextMedium>
            </TextMedium>
          }
          icon={<Icon icon={IconUser}></Icon>}
          description="Complete freedom with unlimited access to build websites of any size."
          offer={
            <OfferDetails
              table={table}
              currentPlanKey={currentPlanKey}
              offerPlanKey={yearly ? 'proYearly' : 'proMonthly'}
              tagline="/month for one person"
              variant="primary"
              getHref={getHref}
              getOnClick={getOnClick}
            />
          }
          features={
            <FeatureList
              features={[
                { label: 'Unlimited Projects' },
                { label: 'Unlimited Pages' },
                { label: 'Share - Commenting' },
                {
                  label: 'Export - Figma, Webflow & React',
                },
                { label: '1,000+ Webflow components' },
                { label: '1,000+ React components' },
                { label: '1,000+ Figma components' },
              ]}
            />
          }
          personaAvatarSrc={[planAvatarPro]}
          personaTitle="Full-time Freelancer"
          personaDescription="An experienced freelancer managing larger projects with more pages and ongoing client maintenance, requiring more project slots"
        />
        <Plan
          variant="secondary"
          title={
            <TextMedium>
              Relume{' '}
              <TextMedium color="gradient" tag="span">
                Team
              </TextMedium>
            </TextMedium>
          }
          icon={<Icon icon={IconGroup} size={20} />}
          description="Bring your team over with all Pro access and enhanced collaboration."
          offer={
            <OfferDetails
              table={table}
              currentPlanKey={currentPlanKey}
              offerPlanKey={yearly ? 'teamYearly' : 'teamMonthly'}
              tagline="/month per person (Min 3 people)"
              variant="primary"
              getHref={getHref}
              getOnClick={getOnClick}
            />
          }
          features={
            <div className={styles.teamFeaturesContainer}>
              <Rows>
                <TextSmall color="grey">Everything In Pro +</TextSmall>
                <FeatureList
                  features={[
                    { label: '3 users included' },
                    { label: 'Each user with Pro features' },
                    { label: 'Team workspace' },
                    { label: 'Branded sharing' },
                  ]}
                />
              </Rows>
            </div>
          }
          personaAvatarSrc={[planAvatarTeam1, planAvatarTeam2, planAvatarTeam3]}
          personaTitle="Agency or Team"
          personaDescription="Collaborates with a team on multiple large website projects, benefiting from unlimited projects and extra membership seats"
        />
      </div>
    </div>
  );
}

function Logo({
  svg: Svg,
}: {
  svg: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
  return <Svg className={styles.logo} />;
}

function Plan({
  variant,
  title,
  icon,
  description,
  offer,
  features,
  personaAvatarSrc,
  personaTitle,
  personaDescription,
}: {
  variant: 'primary' | 'secondary' | 'tertiary';
  title: JSX.Element;
  icon: JSX.Element;
  description: string;
  offer: JSX.Element;
  features: JSX.Element;
  personaAvatarSrc: [string, ...string[]];
  personaTitle: string;
  personaDescription: string;
}) {
  return (
    <div className={classNames(styles.card, styles[variant])}>
      <Rows spacing={24}>
        <Inline xalign="space-between" yalign="end">
          <div className={styles.planName}>{title}</div>
          <div className={styles.icon}>{icon}</div>
        </Inline>
        <TextSmall>{description}</TextSmall>
        {offer}
        <DividerLine />
        {features}
        <Rows spacing={12}>
          <Rows spacing={6}>
            <Inline spacing={10}>
              {personaAvatarSrc.map((src, index) => (
                <img
                  key={index}
                  className={styles.avatar}
                  src={src}
                  alt="Persona avatar"
                />
              ))}
            </Inline>
            <TextSmall>{personaTitle}</TextSmall>
          </Rows>
          <TextSmallLight>{personaDescription}</TextSmallLight>
        </Rows>
      </Rows>
    </div>
  );
}

function FeatureList({
  features,
}: {
  features: { label: string; iconsRight?: JSX.Element }[];
}) {
  return (
    <Rows spacing={8}>
      {features.map(({ label, iconsRight }, index) => (
        <Inline key={index} xalign="space-between" yalign="center" spacing={0}>
          <Inline xalign="start" yalign="center" spacing={10}>
            <Icon icon={IconCheck} />
            <TextSmall>{label}</TextSmall>
          </Inline>
          {iconsRight}
        </Inline>
      ))}
    </Rows>
  );
}

function OfferDetails({
  table,
  offerPlanKey,
  currentPlanKey,
  tagline,
  variant,
  getOnClick,
  getHref,
}: {
  table: PricingTable | undefined;
  currentPlanKey: PricingTableKey | undefined;
  offerPlanKey: PricingTableKey;
  tagline: string;
  variant: 'primary' | 'secondary';
  getOnClick?: (planKey: PricingTableKey) => () => void;
  getHref?: (planKey: PricingTableKey) => string;
}) {
  if (table == null) {
    return (
      <div
        className={styles.loading}
        style={{ height: '124px', width: '100%' }}
      />
    );
  }
  const offer = table[offerPlanKey];

  const variantProps =
    variant === 'primary'
      ? ({
          variant,
          gradient: true,
        } as const)
      : ({
          variant,
        } as const);

  const buttonProps = {
    size: 'large',
    href: getHref?.(offerPlanKey),
    onClick: getOnClick?.(offerPlanKey),
  } as const;
  const currentInterval = getBillingInterval(currentPlanKey);
  const offerInterval = getBillingInterval(offerPlanKey);
  const offerPlanName = getPlanName(offerPlanKey);

  let button;
  if (offer.status === 'unavailable') {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label="Unavailable"
        tooltip={offer.reason}
        disabled
      />
    );
  } else if (offerPlanKey === currentPlanKey) {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label="Current plan"
        tooltip="You're already on this plan"
        disabled
      />
    );
  } else if (currentInterval === 'monthly' && offerInterval === 'yearly') {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label="Change to Yearly Billing"
      />
    );
  } else if (currentInterval === 'yearly' && offerInterval === 'monthly') {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label="Change to Monthly Billing"
      />
    );
  } else if (currentPlanKey == null) {
    const buttonLabel =
      offer.centsPerMemberPerMonth === 0
        ? 'Get started'
        : 'Start 7-day free trial';
    // not currently logged in
    button = <Button {...buttonProps} {...variantProps} label={buttonLabel} />;
  } else if (offer.trialPeriodDays > 0) {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label={`Continue with ${getHumanPlanName(offerPlanName)}`}
      />
    );
  } else if (offer.isUpgrade) {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label={`Upgrade to ${getHumanPlanName(offerPlanName)}`}
      />
    );
  } else {
    button = (
      <Button
        {...buttonProps}
        {...variantProps}
        label={`Downgrade to ${getHumanPlanName(offerPlanName)}`}
      />
    );
  }
  return (
    <Rows spacing={24}>
      <Rows spacing={0}>
        <Inline spacing={4} yalign="start">
          {offer.originalCentsPerMemberPerMonth &&
            offer.originalCentsPerMemberPerMonth !==
              offer.centsPerMemberPerMonth && (
              <Title1 tag="span" color="lightGrey1">
                <s>
                  {formatCentAmount(offer.originalCentsPerMemberPerMonth, {
                    wholeDollars: true,
                  })}
                </s>
              </Title1>
            )}
          <Title1 tag="span">
            {formatCentAmount(offer.centsPerMemberPerMonth, {
              wholeDollars: true,
            })}
          </Title1>
          <span className={styles.currency}>
            <TextMedium tag="span">USD</TextMedium>
          </span>
        </Inline>
        <TextSmallLight>{tagline}</TextSmallLight>
      </Rows>
      {button}
    </Rows>
  );
}

export function getCurrentPlanKey(
  table: PricingTable,
): keyof PricingTable | undefined {
  return (Object.keys(table) as (keyof PricingTable)[]).find((pk) => {
    const plan = table[pk];
    return plan?.status === 'available' && plan.isCurrentPlan;
  });
}
